import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuctionApi } from "../../api/auctions";
import { useBillingApi } from "../../api/billing";
import { useAuth } from "../../providers/AuthContext";
import { Auction } from "../../api/types";
import { isSubscriptionActive } from "../../utils/subscription";
import { useStableCallbacks } from "../../hooks/useStableCallback";

export const useHomePage = () => {
  const { fetchPublicRecentAuctions } = useStableCallbacks(useAuctionApi());
  const { getSummary } = useStableCallbacks(useBillingApi());
  const { isAuthenticated } = useAuth();

  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubscriptionStatusActive, setIsSubscriptionStatus] = useState(false);

  const loadAuctions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPublicRecentAuctions(20);

      const auctionsList = Array.isArray(data)
        ? data
        : ((data as any)?.items ?? []);
      setAuctions(auctionsList);

      if (isAuthenticated) {
        const summary = await getSummary();
        setIsSubscriptionStatus(isSubscriptionActive(summary.status));
      } else {
        // optional: ensure correct state when logged out
        setIsSubscriptionStatus(false);
      }

      return auctionsList;
    } catch (error) {
      console.error("Error fetching auctions:", error);
      setAuctions([]);
      setIsSubscriptionStatus(false);
      return [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, fetchPublicRecentAuctions, getSummary]);

  useEffect(() => {
    void loadAuctions();
  }, [loadAuctions]); // runs on mount + when auth changes

  const auctionsPreview = useMemo(() => auctions.slice(0, 6), [auctions]);

  return {
    auctionsPreview,
    isSubscriptionStatusActive,
    isAuthenticated,
    loading,
    reload: loadAuctions, // optional
  };
};
